import React, { useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase-config';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  updateProfile,
  getRedirectResult
} from 'firebase/auth';
import { db } from '../firebase-config';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Firebase Config Validation
  useEffect(() => {
    const validateFirebaseConfig = () => {
      const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;

      if (!apiKey) {
        const error = 'Firebase API Key nicht in .env gefunden';
        console.error(error);
        setConfigError(error);
        return false;
      }

      if (apiKey === 'AIzaSyBj9-KTnHVkGHDtPwvwdtLiIXt_yGDarHk') {
        const error = 'Standard-API-Key erkannt - Bitte durch eigenen API-Key ersetzen';
        console.error(error);
        setConfigError(error);
        return false;
      }

      const apiKeyPattern = /^AIza[0-9A-Za-z-_]{35}$/;
      if (!apiKeyPattern.test(apiKey)) {
        const error = 'Firebase API Key hat nicht das erwartete Format';
        console.error(error);
        setConfigError(error);
        return false;
      }

      console.log('Firebase API Key Validierung erfolgreich');
      setConfigError(null);
      return true;
    };

    validateFirebaseConfig();
  }, []);

  // Debug-Logging Hilfsfunktion
  const logDebug = useCallback((message, data) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log(message, data);

      // Extra logging for Firebase config
      if (message === 'Firebase Config') {
        console.log('API Key aus .env:', {
          keyExists: !!process.env.REACT_APP_FIREBASE_API_KEY,
          keyLength: process.env.REACT_APP_FIREBASE_API_KEY?.length,
          firstChars: process.env.REACT_APP_FIREBASE_API_KEY?.substring(0, 6) + '...'
        });
      }
    }
  }, []);

  // Fehlerbehandlung Hilfsfunktion
  const handleAuthError = useCallback((error, context) => {
    console.error(`${context}-Fehler:`, error);

    if (error.code === 'auth/api-key-not-valid') {
      const detailedError = {
        message: 'Firebase API Key ist ungültig',
        context,
        apiKeyExists: !!process.env.REACT_APP_FIREBASE_API_KEY,
        environmentMode: process.env.NODE_ENV
      };
      console.error('Detaillierte API Key Fehlerinformationen:', detailedError);
      setAuthError(detailedError.message);
    } else {
      setAuthError(error.message);
    }

    throw error;
  }, []);

  // Hilfsfunktion zum Überprüfen der photoURL
  const validatePhotoURL = useCallback((photoURL) => {
    logDebug('validatePhotoURL aufgerufen mit:', photoURL);

    if (!photoURL) {
      logDebug('Keine photoURL übergeben');
      return null;
    }

    try {
      const url = new URL(photoURL);

      if (url.hostname.includes('googleusercontent.com')) {
        logDebug('Gültige Google-URL gefunden:', url.toString());
        return photoURL;
      }

      logDebug('Gültige URL gefunden:', url.toString());
      return photoURL;
    } catch (e) {
      console.warn('Ungültige photoURL:', photoURL, e);
      return null;
    }
  }, [logDebug]);

  // Basis-Login-Handler
  const handleUserLogin = useCallback(async (user) => {
    setAuthError(null);
    try {
      if (user) {
        logDebug('Login mit User:', {
          uid: user.uid,
          displayName: user.displayName,
          photoURL: user.photoURL,
          isAnonymous: user.isAnonymous,
          profilePath: user.profilePath
        });

        const validPhotoURL = validatePhotoURL(user.photoURL);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.exists() ? userDoc.data() : null;

        const updatedUser = {
          ...user,
          photoURL: validPhotoURL,
          profilePath: user.profilePath || userData?.profilePath,
          hasRpmAvatar: userData?.hasRpmAvatar || false,
          rpmModelUrl: userData?.rpmModelUrl || null
        };

        logDebug('Updated user data:', updatedUser);
        setCurrentUser(updatedUser);
      }
    } catch (error) {
      handleAuthError(error, 'Login');
    }
  }, [logDebug, validatePhotoURL, handleAuthError]);

  // E-Mail/Passwort Login
  const loginWithEmail = async (email, password) => {
    setAuthError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await handleUserLogin(result.user);
    } catch (error) {
      handleAuthError(error, 'E-Mail-Login');
    }
  };

  // E-Mail/Passwort Registrierung
  const registerWithEmail = async (email, password, displayName) => {
    setAuthError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      await handleUserLogin(result.user);
    } catch (error) {
      handleAuthError(error, 'Registrierung');
    }
  };

  // Gast-Login
  const loginAsGuest = async () => {
    setAuthError(null);
    try {
      const result = await signInAnonymously(auth);
      const guestNumber = Math.floor(Math.random() * 9999) + 1;
      const guestName = `Gast${guestNumber.toString().padStart(4, '0')}`;

      await updateProfile(result.user, {
        displayName: guestName,
        photoURL: null
      });

      const updatedUser = {
        ...result.user,
        displayName: guestName
      };

      setCurrentUser(updatedUser);
      return updatedUser;
    } catch (error) {
      handleAuthError(error, 'Gast-Login');
    }
  };

  // Google-Authentifizierung
  const loginWithGoogle = async () => {
    setAuthError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      const result = await signInWithPopup(auth, provider);
      await handleUserLogin(result.user);
      return result.user;
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return null;
      }
      handleAuthError(error, 'Google-Login');
    }
  };

  // Abmelden
  const logout = async () => {
    setAuthError(null);
    try {
      await signOut(auth);
      setCurrentUser(null);
    } catch (error) {
      handleAuthError(error, 'Logout');
    }
  };

  // Überprüfe auf Redirect-Ergebnis beim Laden
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await handleUserLogin(result.user);
        }
      } catch (error) {
        handleAuthError(error, 'Redirect');
      }
    };

    checkRedirectResult();
  }, [handleUserLogin, handleAuthError]);

  // Hilfsfunktion zum Laden der Benutzerdaten aus Firestore
  const loadUserData = async (user) => {
    if (!user) return null;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          ...user,
          profilePath: userData.profilePath,
          hasRpmAvatar: userData.hasRpmAvatar || false,
          rpmModelUrl: userData.rpmModelUrl || null
        };
      }
      return user;
    } catch (error) {
      console.error('Fehler beim Laden der Benutzerdaten:', error);
      return user;
    }
  };

  // Auth State Listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const enrichedUser = await loadUserData(user);
        setCurrentUser(enrichedUser);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    authError,
    configError,
    loginWithEmail,
    registerWithEmail,
    loginAsGuest,
    loginWithGoogle,
    logout
  };

  // Show error message if there's a configuration error
  if (configError) {
    return (
        <div className="text-red-600 p-4">
          Konfigurationsfehler: {configError}
        </div>
    );
  }

  return (
      <AuthContext.Provider value={value}>
        {!loading && children}
      </AuthContext.Provider>
  );
}